import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import ActionButton from "../ui/ActionButton";
import "../../style/ColumnVisibilityMenu.css";

const ColumnVisibilityMenu = ({
  columnVisibility,
  onColumnVisibilityChange,
  onOpenChange,
  darkMode,
  variant = "default",
  isRequestsTable = false, // new prop to differentiate between tables
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showSavedFeedback, setShowSavedFeedback] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  // column definitions based on table type
  const materialsColumns = [
    { field: "material", headerName: t("Material"), mandatory: true },
    { field: "quantity", headerName: t("Quantity Total") },
    { field: "remaining_qty", headerName: t("Quantity Left") },
    { field: "cost", headerName: t("Cost") },
    { field: "status", headerName: t("Status") },
    { field: "latest_pickup", headerName: t("Pickup Deadline") },
    { field: "delivery_instructions", headerName: t("Delivery Instructions") },
    { field: "date_posted", headerName: t("Date Posted") },
  ];

  const requestsColumns = [
    { field: "id", headerName: t("Request #"), mandatory: true },
    { field: "status", headerName: t("Status"), mandatory: true },
    { field: "material", headerName: t("Material") },
    { field: "receiver_company", headerName: t("Requester") },
    { field: "quantity_range", headerName: t("Requested Qty") },
    { field: "allocated_qty", headerName: t("Allocated Qty") },
    { field: "scheduled_date", headerName: t("Pickup Date") },
    { field: "time", headerName: t("Pickup Time") },
    { field: "allocated_cost", headerName: t("Final Revenue") },
    { field: "date_modified", headerName: t("Request Made") },
    { field: "listing_id", headerName: t("Listing #") },
  ];

  const columns = isRequestsTable ? requestsColumns : materialsColumns;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 6,
        left: rect.right + window.scrollX,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleCheckboxChange = (field, isMandatory) => {
    // prevent unchecking mandatory fields
    if (isMandatory && columnVisibility[field] !== false) {
      return;
    }

    onColumnVisibilityChange({
      ...columnVisibility,
      [field]: !columnVisibility[field],
    });
  };

  const handleSaveAsDefault = () => {
    // save current visibility to localStorage with correct key
    const storageKey = isRequestsTable
      ? "requestsTable_columnVisibility"
      : "materialsTable_columnVisibility";
    localStorage.setItem(storageKey, JSON.stringify(columnVisibility));

    setShowSavedFeedback(true);
    setTimeout(() => {
      setShowSavedFeedback(false);
    }, 2000);

    setIsOpen(false);
  };

  const handleResetToDefault = () => {
    const defaultVisibility = isRequestsTable
      ? {
          id: true,
          status: true,
          material: true,
          receiver_company: true,
          quantity_range: true,
          allocated_qty: true,
          scheduled_date: true,
          time: false,
          allocated_cost: true,
          date_modified: true,
          listing_id: false,
        }
      : {
          material: true,
          quantity: false,
          remaining_qty: false,
          cost: true,
          latest_pickup: false,
          status: true,
          date_posted: true,
          delivery_instructions: false,
        };

    onColumnVisibilityChange(defaultVisibility);
  };

  return (
    <div className="column-visibility-menu" ref={menuRef}>
      <span ref={btnRef}>
        <ActionButton
          variant={variant}
          icon={faFilter}
          iconOnly
          tooltip={t("Filters")}
          onClick={handleToggle}
        />
      </span>

      {isOpen && createPortal(
        <div
          className={`column-menu-dropdown ${darkMode ? "dark-mode" : ""}`}
          style={{ position: "absolute", top: menuPos.top, left: menuPos.left, transform: "translateX(-100%)", zIndex: 9999 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="column-menu-header">{t("Filters")}</div>
          <div className="column-menu-items">
            {columns.map((column) => (
              <label
                key={column.field}
                className={`column-menu-item ${
                  column.mandatory ? "mandatory" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleCheckboxChange(column.field, column.mandatory);
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    column.mandatory || columnVisibility[column.field] !== false
                  }
                  onChange={() => {}}
                  onClick={(e) => e.stopPropagation()}
                  disabled={column.mandatory}
                />
                <span className="column-label">{column.headerName}</span>
                {column.mandatory && (
                  <span className="mandatory-badge">{t("Required")}</span>
                )}
              </label>
            ))}
          </div>

          {/* footer with save as default and reset buttons */}
          <div className="column-menu-footer">
            <button
              className="save-default-button"
              onClick={(e) => {
                e.preventDefault();
                handleSaveAsDefault();
              }}
            >
              {showSavedFeedback ? t("Saved!") : t("Save as Default")}
            </button>
            <button
              className="reset-button"
              onClick={(e) => {
                e.preventDefault();
                handleResetToDefault();
              }}
            >
              {t("Reset")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

ColumnVisibilityMenu.propTypes = {
  columnVisibility: PropTypes.object.isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func,
  darkMode: PropTypes.bool,
  variant: PropTypes.string,
  isRequestsTable: PropTypes.bool,
};

export default ColumnVisibilityMenu;
